import type { Plan, PlanType } from '@otter-ai/recall-client';
import { BillingCycle } from '@otter-ai/telemetry';
import type { useCheckout } from '@stripe/react-stripe-js';

type CheckoutLineItems = ReturnType<typeof useCheckout>['lineItems'];
type CheckoutTotal = ReturnType<typeof useCheckout>['total'];

export type CheckoutAnalyticsData = {
  SeatCount: number;
  BillingCycle: BillingCycle;
  SalesTaxAmount: number;
  PriceDisplayed: number;
  CreditAmount: number;
  CheckoutType: 'reactivate' | 'upgrade';
  PlanType?: PlanType;
};

type GetCheckoutAnalyticsParams = {
  total: CheckoutTotal;
  lineItems: CheckoutLineItems;
  selectedPlan: Plan;
  minorUnitsAmountDivisor: number;
  checkoutType: 'reactivate' | 'upgrade';
};

/**
 * Extracts analytics data from checkout session for tracking events
 */
export function getCheckoutAnalyticsData({
  total,
  lineItems,
  selectedPlan,
  minorUnitsAmountDivisor,
  checkoutType,
}: GetCheckoutAnalyticsParams): CheckoutAnalyticsData {
  const seatCount = lineItems[0].quantity;
  const billingCycle =
    selectedPlan.payment_period_type === 'month'
      ? BillingCycle.monthly
      : BillingCycle.annually;
  const salesTaxAmount =
    total.taxExclusive.minorUnitsAmount / minorUnitsAmountDivisor;
  const priceDisplayed = total.total.minorUnitsAmount / minorUnitsAmountDivisor;
  const creditAmount =
    (total.appliedBalance?.minorUnitsAmount ?? 0) / minorUnitsAmountDivisor;

  return {
    SeatCount: seatCount,
    BillingCycle: billingCycle,
    SalesTaxAmount: salesTaxAmount,
    PriceDisplayed: priceDisplayed,
    CreditAmount: creditAmount,
    CheckoutType: checkoutType,
    PlanType: selectedPlan.type,
  };
}

export function isCheckoutDataReady(
  total?: CheckoutTotal,
  lineItems?: CheckoutLineItems,
  selectedPlan?: Plan,
  sessionIsUpdating?: boolean,
): boolean {
  return Boolean(
    total?.total?.minorUnitsAmount !== undefined &&
      total?.taxExclusive?.minorUnitsAmount !== undefined &&
      total?.appliedBalance?.minorUnitsAmount !== undefined &&
      lineItems?.length &&
      selectedPlan &&
      !sessionIsUpdating,
  );
}
